import { useState } from 'react';
import ScrollReveal from '@/components/ScrollReveal';
import Lightbox from '@/components/Lightbox';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Bed, Wifi, Wind, Tv, WashingMachine, Waves, Eye, UtensilsCrossed, Star } from 'lucide-react';
import hera1 from '@/assets/hera-1.jpg';
import hera2 from '@/assets/hera-2.jpg';
import hera3 from '@/assets/hera-3.jpg';
import hera4 from '@/assets/hera-4.jpg';
import hera5 from '@/assets/hera-5.jpg';
import hera6 from '@/assets/hera-6.jpg';
import heraBed1 from '@/assets/hera-bed1.jpg';
import heraBed2 from '@/assets/hera-bed2.jpg';
import heraBed3 from '@/assets/hera-bed3.jpg';
import heraBed4 from '@/assets/hera-bed4.jpg';
import heraBed5 from '@/assets/hera-bed5.jpg';
import heraBed6 from '@/assets/hera-bed6.jpg';
import heraHero from '@/assets/hera-hero.jpg';
import balconyViewSivota from '@/assets/balcony-view-sivota.jpg';

const allImages = [heraHero, balconyViewSivota, hera1, hera2, hera3, hera4, hera5, hera6, heraBed1, heraBed2, heraBed3, heraBed4, heraBed5, heraBed6];

export default function HeraApartment() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const { t } = useLanguage();
  const openLightbox = (img: string) => setLightboxIndex(allImages.indexOf(img));

  const highlights = [
    { icon: Bed, label: t('twoBedrooms') }, { icon: Eye, label: t('seaCorfuViews') },
    { icon: UtensilsCrossed, label: t('fullKitchen') }, { icon: Waves, label: t('privateTerrace') },
    { icon: Wind, label: t('airConditioning') }, { icon: Wifi, label: t('highSpeedWiFi') },
    { icon: Tv, label: t('smartTV') }, { icon: WashingMachine, label: t('washingMachine') },
  ];

  const amenityList = [
    t('seaViewBedrooms'), t('premiumBedLinens'), t('fullyEquippedKitchen'), t('privateTerraceAmenity'),
    t('airConditioningAmenity'), t('highSpeedWiFiAmenity'), t('smartTVAmenity'), t('washingMachineAmenity'),
    t('hairDryer'), t('ironIroningBoard'), t('toiletries'), t('beachTowels'),
    t('stoneBuiltDesign'), t('woodenDetails'), t('traditionalArchitecture'), t('modernComforts'),
  ];

  return (
    <div className="bg-background">
      <section className="relative h-screen flex items-end overflow-hidden">
        <img src={heraHero} alt="Hera Apartment — Sivota Royal" className="absolute inset-0 w-full h-full object-cover hero-image-bright" width={1920} height={1080} fetchPriority="high" style={{ animation: 'heroZoom 25s ease-out forwards' }} />
        <div className="hero-gradient absolute inset-0" />
        <div className="relative z-10 section-padding pb-16 md:pb-24 w-full max-w-3xl">
          <p className="text-white/80 tracking-[0.4em] uppercase text-xs font-sans mb-4 drop-shadow-md">{t('theApartments')}</p>
          <h1 className="font-serif text-6xl md:text-8xl font-light text-white mb-4 drop-shadow-lg">Hera</h1>
          <p className="text-white/80 text-lg md:text-xl max-w-xl font-light leading-relaxed drop-shadow-md">{t('heraHeroDesc')}</p>
          <div className="flex gap-3 mt-8">
            <Link to="/booking" className="bg-accent text-accent-foreground px-8 py-4 text-sm tracking-[0.15em] uppercase hover:bg-accent/90 transition-colors">{t('checkAvailability')}</Link>
            <Link to="/booking" className="border border-white/40 text-white px-8 py-4 text-sm tracking-[0.15em] uppercase hover:bg-white/10 transition-colors">{t('sendEnquiry')}</Link>
          </div>
        </div>
      </section>

      <section className="bg-secondary border-b border-border">
        <div className="section-padding py-6">
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-3">
            {highlights.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 text-sm text-muted-foreground"><Icon size={16} className="text-golden" /><span>{label}</span></div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding py-24 lg:py-36">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <ScrollReveal>
            <p className="text-luxury mb-6">{t('overview')}</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light mb-8">{t('graceElegance')}</h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-6">{t('heraOverviewDesc1')}</p>
            <p className="text-muted-foreground leading-relaxed mb-8">{t('heraOverviewDesc2')}</p>
            <div className="flex items-center gap-1 text-golden">
              {[...Array(5)].map((_, i) => <Star key={i} size={18} fill="currentColor" />)}
              <span className="text-muted-foreground text-sm ml-2">{t('exceptionalOnBooking')}</span>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <div className="image-zoom aspect-[4/5] cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(hera4)}>
              <img src={hera4} alt="Hera bedroom detail" className="w-full h-full object-cover hero-image-bright" loading="lazy" />
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="bg-secondary"><div className="section-padding py-24 lg:py-32"><div className="max-w-6xl mx-auto">
        <ScrollReveal>
          <p className="text-luxury mb-4">{t('masterBedroom')}</p>
          <h3 className="font-serif text-3xl md:text-4xl font-light mb-4">{t('heraMasterTitle')}</h3>
          <p className="text-muted-foreground leading-relaxed max-w-3xl mb-12">{t('heraMasterDesc')}</p>
        </ScrollReveal>
        <ScrollReveal delay={0.15}><div className="grid grid-cols-12 gap-3">
          <div className="col-span-12 image-zoom aspect-[21/9] cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(balconyViewSivota)}><img src={balconyViewSivota} alt="Balcony breakfast panoramic" className="w-full h-full object-cover hero-image-bright" loading="lazy" /></div>
          <div className="col-span-4 image-zoom aspect-[4/3] cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(hera1)}><img src={hera1} alt="Master bedroom" className="w-full h-full object-cover hero-image-bright" loading="lazy" /></div>
          <div className="col-span-4 image-zoom aspect-[4/3] cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(hera2)}><img src={hera2} alt="Bedroom view" className="w-full h-full object-cover hero-image-bright" loading="lazy" /></div>
          <div className="col-span-4 image-zoom aspect-[4/3] cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(hera3)}><img src={hera3} alt="Bedroom sunset" className="w-full h-full object-cover hero-image-bright" loading="lazy" /></div>
        </div></ScrollReveal>
      </div></div></section>

      <section className="section-padding py-24 lg:py-32"><div className="max-w-6xl mx-auto">
        <ScrollReveal>
          <p className="text-luxury mb-4">{t('secondaryBedroom')}</p>
          <h3 className="font-serif text-3xl md:text-4xl font-light mb-4">{t('heraSecondaryTitle')}</h3>
          <p className="text-muted-foreground leading-relaxed max-w-3xl mb-12">{t('heraSecondaryDesc')}</p>
        </ScrollReveal>
        <ScrollReveal delay={0.15}><div className="grid grid-cols-12 gap-3">
          <div className="col-span-7 image-zoom aspect-[16/10] cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(hera4)}><img src={hera4} alt="Secondary bedroom" className="w-full h-full object-cover hero-image-bright" loading="lazy" /></div>
          <div className="col-span-5 flex flex-col gap-3">
            <div className="flex-1 image-zoom cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(hera5)}><img src={hera5} alt="Bedroom detail" className="w-full h-full object-cover hero-image-bright" loading="lazy" /></div>
            <div className="flex-1 image-zoom cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(hera6)}><img src={hera6} alt="Bedroom view" className="w-full h-full object-cover hero-image-bright" loading="lazy" /></div>
          </div>
        </div></ScrollReveal>
      </div></section>

      <section className="bg-secondary"><div className="section-padding py-24 lg:py-32"><div className="max-w-6xl mx-auto">
        <ScrollReveal>
          <p className="text-luxury mb-4">{t('kitchenDining')}</p>
          <h3 className="font-serif text-3xl md:text-4xl font-light mb-4">{t('heraKitchenTitle')}</h3>
          <p className="text-muted-foreground leading-relaxed max-w-3xl mb-12">{t('heraKitchenDesc')}</p>
        </ScrollReveal>
        <ScrollReveal delay={0.15}><div className="grid grid-cols-12 gap-3">
          <div className="col-span-8 image-zoom aspect-[16/9] cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(heraBed2)}><img src={heraBed2} alt="Kitchen overview" className="w-full h-full object-cover hero-image-bright" loading="lazy" /></div>
          <div className="col-span-4 image-zoom aspect-[16/9] cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(heraBed3)}><img src={heraBed3} alt="Kitchen detail" className="w-full h-full object-cover hero-image-bright" loading="lazy" /></div>
          <div className="col-span-4 image-zoom aspect-[4/3] cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(heraBed4)}><img src={heraBed4} alt="Stone bar" className="w-full h-full object-cover hero-image-bright" loading="lazy" /></div>
          <div className="col-span-4 image-zoom aspect-[4/3] cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(heraBed5)}><img src={heraBed5} alt="Dining area" className="w-full h-full object-cover hero-image-bright" loading="lazy" /></div>
          <div className="col-span-4 image-zoom aspect-[4/3] cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(heraBed6)}><img src={heraBed6} alt="Kitchen window view" className="w-full h-full object-cover hero-image-bright" loading="lazy" /></div>
        </div></ScrollReveal>
      </div></div></section>

      <section className="section-padding py-24 lg:py-32"><div className="max-w-6xl mx-auto">
        <ScrollReveal>
          <p className="text-luxury mb-4">{t('livingRoom')}</p>
          <h3 className="font-serif text-3xl md:text-4xl font-light mb-4">{t('heraLivingTitle')}</h3>
          <p className="text-muted-foreground leading-relaxed max-w-3xl mb-12">{t('heraLivingDesc')}</p>
        </ScrollReveal>
        <ScrollReveal delay={0.15}><div className="grid grid-cols-12 gap-3">
          <div className="col-span-6 image-zoom aspect-[4/3] cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(heraBed1)}><img src={heraBed1} alt="Living room" className="w-full h-full object-cover hero-image-bright" loading="lazy" /></div>
          <div className="col-span-6 image-zoom aspect-[4/3] cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(hera6)}><img src={hera6} alt="Living room detail" className="w-full h-full object-cover hero-image-bright" loading="lazy" /></div>
        </div></ScrollReveal>
      </div></section>

      <section><ScrollReveal>
        <div className="image-zoom aspect-[21/9] cursor-pointer" onClick={() => openLightbox(heraHero)}>
          <img src={heraHero} alt="Hera panoramic view" className="w-full h-full object-cover hero-image-bright" loading="lazy" />
        </div>
      </ScrollReveal></section>

      <section className="bg-secondary"><div className="section-padding py-20"><div className="max-w-6xl mx-auto">
        <ScrollReveal>
          <p className="text-luxury mb-6">{t('amenities')}</p>
          <h3 className="font-serif text-3xl font-light mb-10">{t('everythingYouNeed')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {amenityList.map(a => (
              <div key={a} className="bg-background px-5 py-4 text-sm text-foreground border border-border/60 rounded-sm">{a}</div>
            ))}
          </div>
        </ScrollReveal>
      </div></div></section>

      <section className="relative py-28 lg:py-36">
        <img src={heraHero} alt="" className="absolute inset-0 w-full h-full object-cover hero-image-bright" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-black/10" />
        <div className="relative z-10 text-center section-padding">
          <ScrollReveal>
            <p className="text-white/60 tracking-[0.3em] uppercase text-xs mb-4">{t('bookYourStay')}</p>
            <h3 className="font-serif text-4xl md:text-5xl font-light text-white mb-4">{t('readyExperience')} Hera?</h3>
            <p className="text-white/70 max-w-lg mx-auto mb-10">{t('secureYourDates')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/booking" className="bg-accent text-accent-foreground px-10 py-4 text-sm tracking-[0.15em] uppercase hover:bg-accent/90 transition-colors">{t('checkAvailability')}</Link>
              <Link to="/booking" className="border border-white/40 text-white px-10 py-4 text-sm tracking-[0.15em] uppercase hover:bg-white/10 transition-colors">{t('sendEnquiry')}</Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {lightboxIndex !== null && <Lightbox images={allImages} initialIndex={lightboxIndex} onClose={() => setLightboxIndex(null)} />}
    </div>
  );
}
